import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast, toastError } from '../../utils/toast';
import { format, isToday } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { generateTrainingPlanPDF } from '../../utils/pdfGenerator';
import { debugLog, debugError } from '../../utils/debug';

const matchesIsoDate = (dateA, dateB) => {
  if (!dateA || !dateB) return false;
  try {
    return new Date(dateA).toISOString() === new Date(dateB).toISOString();
  } catch (_err) {
    return false;
  }
};

const WeekRowBody = React.forwardRef((props, ref) => {
  const {
    day,
    allRuns,
    visibleRuns,
    resolveRowBackground,
    isCurrentDay,
    isSunday,
    openRunModal,
    getWorkoutTypeInfo,
    renderIcon,
    getAiTip,
    onboardingData,
    getCurrentWeekRegimenData,
    listeners,
    attributes,
    style,
    isOver = false,
    pendingSwap = null,
    dragSource = null,
    dragTarget = null,
    swapFeedback = null,
    swapStatus = { loading: false, error: null },
    onConfirmSwap = () => {},
    onCancelSwap = () => {},
    isSwapEnabled = false,
    index = -1
  } = props;

  // Detect touch devices to disable hover effects on iOS
  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(hover: none)').matches || 
           /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const attributeProps = attributes ? { ...attributes } : {};
  const listenerProps = listeners ? { ...listeners } : {};
  const combinedStyle = {
    ...(attributeProps.style || {}),
    ...(style || {})
  };
  if (attributeProps.style) {
    delete attributeProps.style;
  }
  const finalStyle = Object.keys(combinedStyle).length > 0 ? combinedStyle : undefined;

  const isoDay = day.toISOString();
  const matchesDate = (candidate) => matchesIsoDate(candidate, isoDay);

  const rowBackgroundClass = resolveRowBackground(visibleRuns ?? allRuns, day);
  const isPendingSourceRow = pendingSwap?.source?.date ? matchesDate(pendingSwap.source.date) : false;
  const isPendingTargetRow = pendingSwap?.target?.date ? matchesDate(pendingSwap.target.date) : false;
  const swapMode = pendingSwap?.mode || 'swap';

  const baseRuns = Array.isArray(visibleRuns) ? visibleRuns : allRuns;
  const baseHasRuns = baseRuns.length > 0;
  const baseRun = baseHasRuns ? baseRuns[0] : null;

  let runToRender = baseRun;
  let dragPlaceholderInfo = null;
  let swapCounterpartBanner = null;

  const isSourceRow = dragSource?.date ? matchesDate(dragSource.date) : false;
  const isTargetRow = dragTarget?.date ? matchesDate(dragTarget.date) : false;
  const isDraggingActive = Boolean(dragSource && dragTarget);
  const showAsTargetRow = isPendingTargetRow || (isDraggingActive && isTargetRow);
  const showAsSourceRow = isPendingSourceRow || (isDraggingActive && isSourceRow);
  const labelDateToShow = day;

  const feedbackTargetDate = swapFeedback?.targetDate || null;
  const feedbackVariant = swapFeedback?.variant || 'info';
  const feedbackMessage = swapFeedback?.message || '';
  const shouldShowFeedback = Boolean(feedbackMessage) && feedbackTargetDate ? matchesDate(feedbackTargetDate) : false;

  if (pendingSwap && pendingSwap.source && pendingSwap.target) {
    const isPendingSourceRowMatch = matchesDate(pendingSwap.source.date);
    const isPendingTargetRowMatch = matchesDate(pendingSwap.target.date);

    if (isPendingSourceRowMatch) {
      runToRender = pendingSwap.target.run || null;
      if (!runToRender) {
        dragPlaceholderInfo = { subtitle: 'Move to Empty Day' };
      }
      if (pendingSwap.target?.date) {
        swapCounterpartBanner = `⇅ ${format(new Date(pendingSwap.target.date), 'EEE')}`;
      }
    } else if (isPendingTargetRowMatch) {
      runToRender = pendingSwap.source.run || null;
      if (!runToRender) {
        dragPlaceholderInfo = { subtitle: 'Move to Empty Day' };
      }
      if (pendingSwap.source?.date) {
        swapCounterpartBanner = `⇅ ${format(new Date(pendingSwap.source.date), 'EEE')}`;
      }
    }
  } else if (dragSource && dragTarget) {
    const isLiveSourceRow = matchesDate(dragSource.date);
    const isLiveTargetRow = matchesDate(dragTarget.date);

    if (isLiveSourceRow) {
      runToRender = dragTarget.run || null;
      if (!runToRender) {
        dragPlaceholderInfo = { subtitle: 'Move to Empty Day' };
      }
      swapCounterpartBanner = `⇅ ${format(new Date(dragTarget.date), 'EEE')}`;
    } else if (isLiveTargetRow) {
      runToRender = dragSource.run || null;
      swapCounterpartBanner = `⇅ ${format(new Date(dragSource.date), 'EEE')}`;
    }
  }

  if (
    !swapCounterpartBanner &&
    (showAsTargetRow || showAsSourceRow) &&
    dragSource?.date &&
    dragTarget?.date
  ) {
    if (isTargetRow) {
      const sourceDate = new Date(dragSource.date);
      swapCounterpartBanner = `⇅ ${format(sourceDate, 'EEE · MMM d')}`;
    } else if (isSourceRow) {
      const targetDate = new Date(dragTarget.date);
      swapCounterpartBanner = `⇅ ${format(targetDate, 'EEE · MMM d')}`;
    }
  }

  const getRestState = () => {
    let isScheduledRestDay = false;
    if (getCurrentWeekRegimenData) {
      const regimenData = getCurrentWeekRegimenData();
      if (regimenData && regimenData.success && regimenData.data) {
        try {
          const daysArr = Array.isArray(regimenData.data.days) ? regimenData.data.days : [];
          const populatedCount = daysArr.filter((d) => d && d.title).length;
          if (populatedCount <= 1) {
            isScheduledRestDay = false;
          } else if (onboardingData && onboardingData.trainingStartDate) {
            const trainingStartDate = new Date(onboardingData.trainingStartDate);
            const dayDate = new Date(day);
            isScheduledRestDay = !(dayDate < trainingStartDate);
          } else {
            isScheduledRestDay = true;
          }
        } catch (_err) {
          isScheduledRestDay = true;
        }
      }
    }
    return isScheduledRestDay;
  };

  const handleRowClick = () => {
    const clickableRun = runToRender || baseRun;
    if (!clickableRun) return;
    openRunModal(clickableRun, clickableRun.type || clickableRun.status || 'planned');
  };

  const isHighlightedRow =
    (isDraggingActive && (isSourceRow || isTargetRow)) ||
    (pendingSwap && (isPendingSourceRow || isPendingTargetRow));
  const isPendingRow = pendingSwap && (isPendingSourceRow || isPendingTargetRow);

  // Borders are owned by the parent container (`divide-y` + outer border).
  // Per-row uses `ring` for active/pending state since `ring` doesn't reserve layout space.
  const rowClasses = [
    'relative flex items-center justify-between w-full transition-all duration-200 px-3.5 py-4 h-[76px]',
    isSwapEnabled ? 'cursor-grab' : 'cursor-pointer',
    isSwapEnabled && listeners ? 'select-none' : '',
    isSunday ? 'rounded-b-xl' : '',
    isCurrentDay ? 'bg-blue-800/40' : rowBackgroundClass,
    // Only show hover effect on non-touch devices (desktop with mouse)
    !isCurrentDay && !isTouchDevice && 'hover:bg-gray-600/40',
    isPendingRow
      ? 'ring-2 ring-blue-400/80 ring-inset bg-blue-500/15'
      : isHighlightedRow
        ? 'ring-2 ring-dashed ring-blue-400/60 ring-inset bg-blue-500/10'
        : '',
    isOver ? 'ring-2 ring-blue-400/70 ring-offset-2 ring-offset-gray-900' : '',
    shouldShowFeedback && feedbackVariant === 'warning' ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-gray-900' : '',
    shouldShowFeedback && feedbackVariant === 'success' ? 'ring-2 ring-emerald-400/70 ring-offset-2 ring-offset-gray-900' : ''
  ].filter(Boolean).join(' ');

  // Use finalStyle (no special inline styles needed - Tailwind classes should work)
  const rowStyle = finalStyle;

  return (
    <div
      ref={ref}
      {...listenerProps}
      {...attributeProps}
      style={{ ...rowStyle, touchAction: 'manipulation' }}
      className={`${rowClasses} calendar-row`}
      data-clickable="true"
      aria-current={isCurrentDay ? 'date' : undefined}
      onClick={handleRowClick}
    >
      <div
        className="flex items-center gap-3 flex-1 min-w-0 h-full"
        style={{ touchAction: 'manipulation' }}
      >
        {(() => {
          if (runToRender) {
            const typeInfo = getWorkoutTypeInfo(runToRender.title, runToRender.subtitle);

            let subtitle = runToRender.subtitle || '';
            if (!subtitle) {
              if (runToRender.distance_km) subtitle = `${runToRender.distance_km}km`;
              else if (runToRender.distance_miles) subtitle = `${runToRender.distance_miles}mi`;
              else if (runToRender.duration_minutes) subtitle = `${runToRender.duration_minutes} min`;
            }

            const aiTip = getAiTip(runToRender, day.getDay(), { forWeekView: true }) || 'Keep consistent effort.';

            return (
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {renderIcon(typeInfo)}
                <div className="flex-1 min-w-0 space-y-[2px]">
                  <p className={`text-base font-semibold ${typeInfo.text} truncate leading-tight calendar-row-text`}>{runToRender.title || 'Workout'}</p>
                  <p className={`text-sm ${typeInfo.text} opacity-80 truncate leading-tight calendar-row-text`}>{subtitle}</p>
                  <p className={`text-sm ${typeInfo.text} opacity-60 truncate whitespace-nowrap overflow-hidden leading-tight calendar-row-text`}>{aiTip}</p>
                </div>
              </div>
            );
          }

          if (dragPlaceholderInfo) {
            return (
              <div className="flex items-center gap-3 flex-1 min-w-0 h-full">
                <div className="w-5 h-5 rounded-full border border-dashed border-blue-300/70 flex items-center justify-center text-blue-200 text-xs font-semibold">↔</div>
                <div className="flex-1 min-w-0 flex flex-col justify-center h-full space-y-[2px]">
                  <p className="text-base font-semibold text-blue-100 truncate font-sans">{dragPlaceholderInfo.subtitle}</p>
                  <p className="text-sm text-blue-200/70 truncate whitespace-nowrap overflow-hidden font-sans">{dragPlaceholderInfo.message}</p>
                </div>
              </div>
            );
          }

          if (!baseHasRuns) {
            const isScheduledRestDay = getRestState();
            if (isScheduledRestDay) {
              const typeInfo = getWorkoutTypeInfo('Rest Day');
              const tip = getAiTip({ title: 'Rest Day', details: '' }, day.getDay(), { forWeekView: true });
              return (
                <div className="flex items-center gap-3 flex-1 min-w-0 h-full">
                  {renderIcon(typeInfo)}
                  <div className="flex-1 min-w-0 flex flex-col justify-center h-full space-y-0">
                    <p className={`text-base font-semibold ${typeInfo.text} truncate font-sans`}>Rest Day</p>
                    <p className={`text-sm ${typeInfo.text} opacity-70 truncate whitespace-nowrap overflow-hidden font-sans`}>{tip}</p>
                  </div>
                </div>
              );
            }

            const typeInfo = getWorkoutTypeInfo('Unscheduled');
            return (
              <div className="flex items-center gap-3 flex-1 min-w-0 h-full">
                {renderIcon(typeInfo)}
                <div className="flex-1 min-w-0 flex flex-col justify-center h-full space-y-0">
                  <p className={`text-base font-semibold ${typeInfo.text} truncate font-sans`}>Unscheduled</p>
                  <p className={`text-sm ${typeInfo.text} opacity-70 truncate whitespace-nowrap overflow-hidden font-sans`}>No training planned</p>
                </div>
              </div>
            );
          }

          return null;
        })()}
      </div>

      <div className="flex-shrink-0 text-right pl-3 space-y-[2px]">
        <p className={`text-base font-bold ${isCurrentDay ? 'text-blue-400' : 'text-gray-300'} font-sans`}>{format(labelDateToShow, 'EEE')}</p>
        <p className={`text-sm ${isCurrentDay ? 'text-blue-300' : 'text-gray-400'} font-sans`}>{format(labelDateToShow, 'MMM d')}</p>
        {swapCounterpartBanner ? (
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-200/80 font-sans">
            {swapCounterpartBanner}
          </p>
        ) : null}
        {isCurrentDay && <p className="text-blue-300 text-sm mt-0.5 font-sans">Today</p>}
      </div>

      {pendingSwap && isPendingTargetRow && (
        <div className="absolute left-1/2 -translate-x-1/2 top-0 flex flex-col items-center gap-1.5 z-20 w-full px-3 pointer-events-none">
          <div className="px-3 py-1 rounded-full bg-blue-500 border border-blue-400/80 text-[11px] font-semibold uppercase tracking-wide text-white pointer-events-auto shadow-md">
            {swapMode === 'move' ? 'Move here?' : 'Swap here?'}
          </div>
          <div className="flex items-center gap-2 bg-gray-900/25 border border-blue-500/40 rounded-full px-3 py-2 shadow-lg backdrop-blur-sm pointer-events-auto">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onConfirmSwap();
              }}
              disabled={swapStatus.loading}
              className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-500/90 hover:bg-blue-500 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {swapStatus.loading ? 'Saving…' : 'Apply'}
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onCancelSwap();
              }}
              disabled={swapStatus.loading}
              className="px-3 py-1 text-xs font-semibold rounded-full bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
          {swapStatus.error && (
            <span className="text-xs text-red-300 pointer-events-auto">{swapStatus.error}</span>
          )}
        </div>
      )}

      {shouldShowFeedback && !pendingSwap && (
        <div className="absolute left-1/2 -translate-x-1/2 top-0 flex flex-col items-center gap-1.5 z-10 w-full px-3 pointer-events-none">
          <div
            className={`px-3 py-1 rounded-full border text-[11px] font-semibold uppercase tracking-wide pointer-events-none shadow-md ${feedbackVariant === 'success'
              ? 'bg-emerald-500/95 border-emerald-300/80 text-white'
              : feedbackVariant === 'warning'
                ? 'bg-amber-500/95 border-amber-300/80 text-gray-900'
                : 'bg-blue-500/95 border-blue-300/80 text-white'
            }`}
          >
            {feedbackMessage}
          </div>
        </div>
      )}
    </div>
  );
});

WeekRowBody.displayName = 'WeekRowBody';

function StaticWeekRow(props) {
  return <WeekRowBody {...props} ref={null} index={props.index} />;
}

function DraggableWeekRow(props) {
  const {
    day,
    index,
    allRuns,
    visibleRuns,
    resolveRowBackground,
    pendingSwap,
    dragSource,
    dragTarget,
    swapFeedback,
    swapStatus,
    onConfirmSwap,
    onCancelSwap,
    isSwapEnabled,
    ...rest
  } = props;

  const primaryRun = allRuns[0] || null;
  const draggableId = day.toISOString();

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef
  } = useDraggable({
    id: draggableId,
    disabled: !primaryRun || Boolean(pendingSwap),
    data: {
      run: primaryRun,
      date: day,
      dayIndex: index,
      regimenId: primaryRun?.regimen_id || null
    }
  });

  const {
    setNodeRef: setDroppableRef,
    isOver
  } = useDroppable({
    id: `droppable-${draggableId}`,
    data: {
      run: primaryRun,
      date: day,
      dayIndex: index,
      regimenId: primaryRun?.regimen_id || null
    }
  });

  const combinedRef = (node) => {
    setDraggableRef(node);
    setDroppableRef(node);
  };

  return (
    <WeekRowBody
      {...rest}
      ref={combinedRef}
      day={day}
      index={index}
      allRuns={allRuns}
      visibleRuns={visibleRuns}
      resolveRowBackground={resolveRowBackground}
      listeners={primaryRun ? listeners : undefined}
      attributes={primaryRun ? attributes : undefined}
      isOver={isOver}
      pendingSwap={pendingSwap}
      dragSource={dragSource}
      dragTarget={dragTarget}
      swapFeedback={swapFeedback}
      swapStatus={swapStatus}
      onConfirmSwap={onConfirmSwap}
      onCancelSwap={onCancelSwap}
      isSwapEnabled={isSwapEnabled}
    />
  );
}

const DragOverlayCard = ({ run, getWorkoutTypeInfo, renderIcon, width }) => {
  const typeInfo = getWorkoutTypeInfo(run.title, run.subtitle);
  return (
    <div className="pointer-events-none" style={{ width: width || '100%' }}>
      <div className="bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 shadow-2xl">
        <div className="flex items-center gap-3">
          {renderIcon(typeInfo)}
          <div className="flex-1 min-w-0 space-y-0">
            <p
              className={`text-base font-semibold ${typeInfo.text} truncate leading-tight`}
              style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', letterSpacing: '-0.025em' }}
            >
              {run.title || 'Workout'}
            </p>
            {run.subtitle && (
              <p
                className={`text-sm ${typeInfo.text} opacity-80 truncate leading-tight`}
                style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', letterSpacing: '-0.025em' }}
              >
                {run.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CalendarGrid = ({ 
  viewMode, 
  viewDays, 
  getRunsForDay, 
  openRunModal, 
  loading = false,
  currentDate = new Date(),
  getCurrentWeekRegimenData = null,
  regimens = [],
  onboardingData = null,
  isSwapEnabled = false,
  onSwapRequest = () => {},
  pendingSwap = null,
  swapFeedback = null,
  swapStatus = { loading: false, error: null },
  onConfirmSwap = () => {},
  onCancelSwap = () => {}
}) => {
  const [isClient, setIsClient] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const [dragOriginRect, setDragOriginRect] = useState(null);

  const isCoarsePointerDevice = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    return mediaQuery.matches;
  }, []);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return closestCenter(args);
  }, []);

  const dropAnimation = useMemo(
    () => ({
      duration: 340,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)'
    }),
    []
  );

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 900,
      tolerance: 6,
      distance: 20
    }
  });
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(
    ...(isCoarsePointerDevice
      ? [touchSensor, keyboardSensor]
      : [pointerSensor, touchSensor, keyboardSensor])
  );

  const handleDragStart = useCallback((event) => {
    const run = event.active?.data?.current?.run || null;
    if (!run) return;

    setActiveDrag({ run });
    const sourcePayload = event.active?.data?.current || null;
    if (sourcePayload?.run) {
      setDragSource({
        run: sourcePayload.run,
        date: sourcePayload.date || null
      });
      setDragTarget(null);
    } else {
      setDragSource(null);
      setDragTarget(null);
    }

    import('../../utils/haptics').then(m => m.haptic('light')).catch(() => {});
  }, []);

  const updateHoverTarget = useCallback((overData) => {
    if (!overData) {
      setDragTarget(null);
      return;
    }

    const nextTarget = {
      run: overData.run || null,
      date: overData.date || null,
      mode: overData.run ? 'swap' : 'move'
    };

    setDragTarget((prev) => {
      if (
        prev &&
        prev.mode === nextTarget.mode &&
        prev.run?.id === nextTarget.run?.id &&
        matchesIsoDate(prev.date, nextTarget.date)
      ) {
        return prev;
      }
      return nextTarget;
    });
  }, []);

  const handleDragMove = useCallback((event) => {
    const sourcePayload = event.active?.data?.current || null;
    if (sourcePayload?.run && (!dragSource || dragSource.run.id !== sourcePayload.run.id)) {
      setDragSource({
        run: sourcePayload.run,
        date: sourcePayload.date || null
      });
    } else if (!sourcePayload?.run && !dragSource?.run) {
      setDragSource(null);
    }

    const overData = event.over?.data?.current || null;
    updateHoverTarget(overData);
  }, [dragSource, updateHoverTarget]);

  const handleDragOver = useCallback((event) => {
    const sourcePayload = event.active?.data?.current || null;
    if (sourcePayload?.run && (!dragSource || dragSource.run.id !== sourcePayload.run.id)) {
      setDragSource({
        run: sourcePayload.run,
        date: sourcePayload.date || null
      });
    } else if (!sourcePayload?.run && !dragSource?.run) {
      setDragSource(null);
    }

    const overData = event.over?.data?.current || null;
    updateHoverTarget(overData);
  }, [dragSource, updateHoverTarget]);

  const handleDragEnd = useCallback((event) => {
    const sourceData = event.active?.data?.current;
    const targetSlot = event.over?.data?.current;

    setDragSource(null);
    setDragTarget(null);

    if (!sourceData?.run || !targetSlot) {
      setActiveDrag(null);
      setDragOriginRect(null);
      return;
    }

    if (sourceData.dayIndex === targetSlot.dayIndex) {
      setActiveDrag(null);
      setDragOriginRect(null);
      return;
    }

    onSwapRequest({ source: sourceData, target: targetSlot });
    setActiveDrag(null);
    setDragOriginRect(null);
  }, [onSwapRequest]);

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    setDragOriginRect(null);
    setDragSource(null);
    setDragTarget(null);
  }, []);

  // PDF generation function using the proper utility
  const handleGeneratePDF = useCallback(async () => {
    try {
      if (!regimens || regimens.length === 0) {
        toastError('No schedule available to export yet');
        return;
      }

      debugLog('CalendarGrid: Generating PDF with regimens:', regimens);
      debugLog('CalendarGrid: Onboarding data available:', onboardingData);
      // Removed verbose onboarding data keys dump

      // Use the enhanced PDF generator from utils with onboarding data
      generateTrainingPlanPDF(regimens, onboardingData);
     
    } catch (error) {
      debugError('Error generating PDF:', error);
      toastError('Could not generate PDF');
    }
  }, [regimens, onboardingData]);

  // Event listener for PDF generation
  useEffect(() => {
    const handlePDFGeneration = () => {
      handleGeneratePDF();
    };

    window.addEventListener('runium:generate-week-pdf', handlePDFGeneration);
    
    return () => {
      window.removeEventListener('runium:generate-week-pdf', handlePDFGeneration);
    };
  }, [handleGeneratePDF]); // Include onboardingData in dependencies

  // Note: Removed font preloading as it was causing transparent images

  // Helper function to get workout type info
  const getWorkoutTypeInfo = (title, subtitle) => {
    const titleLower = (title || '').toLowerCase();
    
    if (titleLower.includes('easy') || titleLower.includes('recovery')) {
      return {
        bg: 'from-green-500/20 via-green-400/15 to-green-500/20 border-green-400/30',
        dot: 'bg-green-400',
        text: 'text-green-200',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
      };
    }
    
    if (titleLower.includes('tempo') || titleLower.includes('threshold')) {
      return {
        bg: 'from-orange-500/20 via-orange-400/15 to-orange-500/20 border-orange-400/30',
        dot: 'bg-orange-400',
        text: 'text-orange-200',
        icon: 'M13 10V3L4 14h7v7l9-11h-7z'
      };
    }
    
    if (titleLower.includes('interval') || titleLower.includes('speed')) {
      return {
        bg: 'from-red-500/20 via-red-400/15 to-red-500/20 border-red-400/30',
        dot: 'bg-red-400',
        text: 'text-red-200',
        icon: 'M13 10V3L4 14h7v7l9-11h-7z'
      };
    }
    
    if (titleLower.includes('long') || titleLower.includes('endurance')) {
      return {
        bg: 'from-blue-500/20 via-blue-400/15 to-blue-500/20 border-blue-400/30',
        dot: 'bg-blue-400',
        text: 'text-blue-200',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
      };
    }
    
    if (titleLower.includes('rest') || titleLower.includes('off')) {
      return {
        bg: 'from-gray-500/20 via-gray-400/15 to-gray-500/20 border-gray-400/30',
        dot: 'bg-gray-400',
        text: 'text-gray-200',
        icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
      };
    }
    
    if (titleLower.includes('unscheduled') || titleLower.includes('no schedule')) {
      return {
        bg: 'from-amber-500/20 via-amber-400/15 to-amber-500/20 border-amber-400/30',
        dot: 'bg-amber-400',
        text: 'text-amber-200',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
      };
    }
    
    // Default for other workouts
    return {
      bg: 'from-purple-500/20 via-purple-400/15 to-purple-500/20 border-purple-400/30',
      dot: 'bg-purple-400',
      text: 'text-purple-200',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z'
    };
  };

  // Helper function to render workout icon
  const renderIcon = (typeInfo) => (
    <div className={`w-5 h-5 ${typeInfo.dot} rounded-full flex items-center justify-center flex-shrink-0`}>
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={typeInfo.icon} />
      </svg>
    </div>
  );

  // Fallback deterministic type-based tip generator (replaces old mocks in most cases)
  const getTypeBasedTip = (workoutType, hash, weekNumber) => {
    // Expanded variants - more options per category
    const variants = {
      hard: [
        'Push quality in intervals.',
        'Recover fully between efforts.',
        'Maintain form as you fatigue.',
        'Hit your target paces.',
        'Focus on quality over quantity.',
        'Nail your intervals today.',
        'Finish each interval strong.',
        'Control breathing during recovery.'
      ],
      tempo: [
        'Sustain threshold pace.',
        'Find your steady rhythm.',
        'Progress to tempo pace.',
        'Hold form as effort increases.',
        'Feel the controlled discomfort.',
        'Lock in your threshold effort.',
        'Stay mentally focused.',
        'Build lactate tolerance.'
      ],
      long: [
        'Pace yourself wisely.',
        'Fuel to sustain effort.',
        'Stay hydrated throughout.',
        'Build endurance steadily.',
        'Start slow, finish strong.',
        'Practice your nutrition plan.',
        'Focus on consistent effort.',
        'Maintain good form when tired.'
      ],
      easy: [
        'Keep conversational pace.',
        'Stay relaxed and easy.',
        'Focus on good form.',
        'Enjoy the easy effort.',
        'Let your body recover.',
        'Keep heart rate low today.',
        'Practice efficient breathing.',
        'Run truly easy, not moderate.'
      ],
      rest: [
        'Recovery is crucial.',
        'Let your body heal.',
        'Active recovery helps.',
        'Sleep well tonight.',
        'Focus on nutrition today.',
        'Stretch gently if needed.',
        'Rest days build fitness too.',
        'Enjoy your day off running.'
      ],
      default: [
        'Listen to your body.',
        'Trust your training.',
        'Focus on your form.',
      ]
    };

    // More variety - use week number to shift starting point
    // This ensures different weeks get different tip rotations
    const adjustedHash = hash + (weekNumber * 31);
    const arr = variants[workoutType] || variants.default;
    return arr[Math.abs(adjustedHash) % arr.length];
  };

  // Helper function to generate dynamic AI tips from workout descriptions
  const getAiTip = (workout, dayIndex, options = {}) => {
    if (!workout) return 'Stay consistent.';
    
    const title = workout.title || '';
    const titleLower = title.toLowerCase();
    
    // Workout type detection (unchanged)
    const workoutType = 
      titleLower.includes('interval') || titleLower.includes('speed') || titleLower.includes('repeat') ? 'hard' :
      titleLower.includes('tempo') || titleLower.includes('threshold') || titleLower.includes('lactate') ? 'tempo' :
      titleLower.includes('long') || titleLower.includes('distance') || titleLower.includes('marathon') ? 'long' :
      titleLower.includes('easy') || titleLower.includes('recovery') || titleLower.includes('light') ? 'easy' :
      titleLower.includes('rest') || titleLower.includes('off') ? 'rest' : 'default';
    
    // Get current date and calculate week number
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const weekNumber = Math.ceil((((today - startOfYear) / 86400000) + startOfYear.getDay() + 1) / 7);
    
    // More diverse hash inputs:
    // 1. Include workout title for consistency across rerenders
    // 2. Include day of week for variety within week
    // 3. Include workout type for context-appropriate tips
    // 4. Include a unique string per day type to avoid repetition
    const typeSpecificSeed = {
      hard: 'intervals',
      tempo: 'threshold',
      long: 'endurance',
      easy: 'recovery',
      rest: 'rest',
      default: 'running'
    }[workoutType];
    
    // Create deterministic but unique hash
    const hashInput = title + '|' + workoutType + '|' + dayIndex + '|' + typeSpecificSeed;
    const hash = hashInput.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    // For rest days on same week, make sure they differ
    const restAdjustment = workoutType === 'rest' ? dayIndex * 7 : 0;
    const finalHash = hash + restAdjustment;
    
    // Simple direct return with week awareness for variety across weeks
    return formatTip(getTypeBasedTip(workoutType, finalHash, weekNumber));
  };

  // Format tip to ensure it fits on mobile (≤35 chars max)
  const formatTip = (tip) => {
    if (!tip) return 'Stay consistent.';
    
    let formatted = tip.trim();
    
    // Make sure it ends with a period
    if (!/[.!?]$/.test(formatted)) {
      formatted += '.';
    }
    
    // Ensure first letter is capitalized
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    
    // Truncate if too long
    if (formatted.length > 35) {
      formatted = formatted.substring(0, 32) + '...';
    }
    
    return formatted;
  };

  // Skeleton loader for week view
const CalendarSkeleton = ({ viewMode, viewDays }) => {
  if (viewMode === 'week') {
    return (
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-700">
          {viewDays.map((day) => (
              <div key={day.toISOString()} className="p-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                  <div className="w-5 h-5 bg-gray-600 rounded" />
                      <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-600 rounded w-3/4" />
                    <div className="h-3 bg-gray-600 rounded w-1/2" />
              </div>
              </div>
                <div className="text-right space-y-2">
                  <div className="h-4 bg-gray-600 rounded w-8" />
                  <div className="h-3 bg-gray-600 rounded w-12" />
                    </div>
            </div>
              </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 gap-px bg-gray-700">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="bg-gray-800 p-2 h-24 animate-pulse">
            <div className="space-y-2">
              <div className="h-4 bg-gray-600 rounded mb-2" />
              <div className="space-y-1">
                <div className="h-3 bg-gray-600 rounded" />
                <div className="h-3 bg-gray-600 rounded" />
                </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

  if (loading) {
    return <CalendarSkeleton viewMode={viewMode} viewDays={viewDays} />;
  }

  if (viewMode === 'week') {
    const resolveRowBackground = (runs, targetDay) => {
                if (runs.length > 0) {
                  const typeInfo = getWorkoutTypeInfo(runs[0].title, runs[0].subtitle);
                  return typeInfo.bg;
                }
                
                let isScheduledRestDay = false;
                if (getCurrentWeekRegimenData) {
                  const regimenData = getCurrentWeekRegimenData();
                  if (regimenData && regimenData.success && regimenData.data) {
                    try {
                      const daysArr = Array.isArray(regimenData.data.days) ? regimenData.data.days : [];
            const populatedCount = daysArr.filter((d) => d && d.title).length;
                      if (populatedCount <= 1) {
                        isScheduledRestDay = false;
            } else if (onboardingData && onboardingData.trainingStartDate) {
                          const trainingStartDate = new Date(onboardingData.trainingStartDate);
              const dayDate = new Date(targetDay);
              isScheduledRestDay = !(dayDate < trainingStartDate);
                          } else {
                            isScheduledRestDay = true;
                          }
          } catch (_err) {
                      isScheduledRestDay = true;
                    }
                  }
                }
                
                return isScheduledRestDay 
                  ? getWorkoutTypeInfo('Rest Day').bg 
                  : getWorkoutTypeInfo('Unscheduled').bg;
              };
              
    const renderRow = (RowComponent) =>
      viewDays.map((day, index) => {
        const dayRuns = getRunsForDay(day);
        const allRuns = [];
        dayRuns.completed.forEach((run) => allRuns.push({ ...run, type: 'completed' }));
        dayRuns.planned.forEach((planned) => {
          if (!planned.associated_actual_run || planned.status === 'skipped') {
            allRuns.push({ ...planned, type: 'planned' });
          }
        });
              
              return (
          <RowComponent
                  key={day.toISOString()}
            day={day}
            index={index}
            allRuns={allRuns}
            visibleRuns={allRuns}
            resolveRowBackground={resolveRowBackground}
            isCurrentDay={isToday(day)}
            isSunday={day.getDay() === 0}
            openRunModal={openRunModal}
            getWorkoutTypeInfo={getWorkoutTypeInfo}
            renderIcon={renderIcon}
            getAiTip={getAiTip}
            onboardingData={onboardingData}
            getCurrentWeekRegimenData={getCurrentWeekRegimenData}
            pendingSwap={pendingSwap}
            dragSource={dragSource}
            dragTarget={dragTarget}
            swapFeedback={swapFeedback}
            swapStatus={swapStatus}
            onConfirmSwap={onConfirmSwap}
            onCancelSwap={onCancelSwap}
            isSwapEnabled={isSwapEnabled}
          />
        );
      });

    const shareSection = (
      <div
        className="flex justify-center pt-2 pb-2"
        data-share-container
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
      >
        <button
          onClick={async (event) => {
            const shareBtn = event.currentTarget;

            try {
              const { default: html2canvas } = await import('html2canvas');
              const weekViewElement = document.querySelector('[data-week-view="calendar"]');

              if (weekViewElement) {
                const footer = weekViewElement.querySelector('[class*="bg-gradient-to-r"][class*="from-gray-800/50"][class*="to-gray-700/50"]');
                const shareButtonContainer = shareBtn.closest('[data-share-container]');
                if (shareBtn) shareBtn.style.display = 'none';
                if (footer) footer.style.display = 'none';
                if (shareButtonContainer) shareButtonContainer.style.display = 'none';

                const watermark = document.createElement('div');
                watermark.className = 'share-watermark';
                watermark.style.cssText = `
                  padding: 16px;
                  text-align: center;
                  font-size: 12px;
                  color: #9ca3af;
                  background: rgba(31, 41, 55, 0.5);
                  border-top: 1px solid rgba(75, 85, 99, 0.3);
                  margin: 0;
                  line-height: 1.3;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                `;
                let dateRangeText = '';
                if (viewDays && viewDays.length >= 7) {
                  const startDate = viewDays[0];
                  const endDate = viewDays[6];
                  dateRangeText = `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
                } else if (viewDays && viewDays[0]) {
                  dateRangeText = format(viewDays[0], 'MMM d, yyyy');
                } else {
                  dateRangeText = new Date().toLocaleDateString();
                }

                watermark.innerHTML = `Generated with Runium.ai • ${dateRangeText}`;
                weekViewElement.appendChild(watermark);

                const truncatedElements = weekViewElement.querySelectorAll('.truncate');
                truncatedElements.forEach((el) => {
                  el.classList.remove('truncate');
                  el.style.whiteSpace = 'normal';
                  el.style.overflow = 'visible';
                  el.style.textOverflow = 'clip';
                });

                const textContainers = weekViewElement.querySelectorAll('.flex-1.min-w-0.space-y-0');
                textContainers.forEach((container) => {
                  container.style.paddingBottom = '4px';
                });

                const allElements = weekViewElement.querySelectorAll('*');
                allElements.forEach((el) => {
                  if (el.style.fontFamily === '') {
                    el.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
                  }
                  el.style.webkitFontSmoothing = 'antialiased';
                  el.style.mozOsxFontSmoothing = 'grayscale';
                });

                const canvas = await html2canvas(weekViewElement, {
                  backgroundColor: '#111827',
                  scale: 2,
                  logging: false,
                  useCORS: true,
                  allowTaint: true
                });

                truncatedElements.forEach((el) => {
                  el.classList.add('truncate');
                  el.style.whiteSpace = '';
                  el.style.overflow = '';
                  el.style.textOverflow = '';
                });
                textContainers.forEach((container) => {
                  container.style.paddingBottom = '';
                });

                const cleanupElements = weekViewElement.querySelectorAll('*');
                cleanupElements.forEach((el) => {
                  if (el.style.fontFamily === '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif') {
                    el.style.fontFamily = '';
                  }
                  el.style.webkitFontSmoothing = '';
                  el.style.mozOsxFontSmoothing = '';
                });

                watermark.remove();
                if (shareBtn) shareBtn.style.display = '';
                if (footer) footer.style.display = '';
                if (shareButtonContainer) shareButtonContainer.style.display = '';

                const dataUrl = canvas.toDataURL('image/png');

                canvas.toBlob(async (blob) => {
                  const fileName = `runium-schedule-${viewDays && viewDays[0] ? format(viewDays[0], 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')}.png`;
                  if (navigator.share) {
                    try {
                      const modal = document.createElement('div');
                      modal.style.cssText = `
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.95);
                        display: flex;
                        flex-direction: column;
                        z-index: 9999;
                      `;
                      modal.innerHTML = `
                        <div style="display: flex; flex-direction: column; height: 100%; padding: 20px; padding-bottom: max(env(safe-area-inset-bottom), 20px);">
                          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                            <h3 style="color: white; font-size: 18px; font-weight: bold;">Your Training Schedule</h3>
                            <button id="close-modal" style="color: white; font-size: 18px; background: none; border: none; padding: 0; width: 30px; height: 30px;">&times;</button>
                          </div>
                          <div style="flex: 1; overflow: auto; display: flex; align-items: center; justify-content: center; min-height: 0;">
                            <img src="${dataUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 8px;" />
                          </div>
                          <div style="margin-top: 20px; padding-bottom: max(env(safe-area-inset-bottom), 20px);">
                            <button id="share-image" style="width: 100%; padding: 14px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 12px; font-size: 16px; font-weight: 600; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
                              Share Schedule
                            </button>
                          </div>
                        </div>
                      `;

                      document.body.appendChild(modal);

                      document.getElementById('close-modal').onclick = () => {
                        document.body.removeChild(modal);
                      };

                      document.getElementById('share-image').onclick = async () => {
                        try {
                          const file = new File([blob], fileName, { type: 'image/png' });
                          await navigator.share({ files: [file] });
                          document.body.removeChild(modal);
                        } catch (shareError) {
                          if (shareError.name !== 'AbortError') {
                            debugError('Share failed:', shareError);
                          }
                        }
                      };

                      modal.onclick = (e) => {
                        if (e.target === modal) {
                          document.body.removeChild(modal);
                        }
                      };
                    } catch (shareError) {
                      debugError('All share methods failed:', shareError);
                      const a = document.createElement('a');
                      a.href = dataUrl;
                      a.download = fileName;
                      a.click();
                    }
                  } else {
                    const a = document.createElement('a');
                    a.href = dataUrl;
                    a.download = fileName;
                    a.click();
                  }
                }, 'image/png');
              }
            } catch (error) {
              debugError('Error sharing week view:', error);
              toastError(`Failed to generate image: ${error.message}`);
            }
          }}
          className="group flex items-center gap-2 px-4 py-2 bg-gray-700/50 hover:bg-gray-600/50 border border-gray-600/30 hover:border-gray-500/50 text-gray-300 hover:text-white rounded-lg font-medium transition-all duration-200 backdrop-blur-sm"
          title="Share your weekly training schedule"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <span className="text-base">Share Schedule</span>
        </button>
      </div>
    );

    if (isSwapEnabled && isClient) {
      return (
        <div className="space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div data-week-view="calendar" className="bg-gray-800 rounded-xl overflow-hidden border-t border-gray-700">
              <div className="divide-y divide-gray-700">
                {renderRow((props) => <DraggableWeekRow {...props} />)}
              </div>
            </div>

            <DragOverlay dropAnimation={dropAnimation} modifiers={[restrictToVerticalAxis]}>
              {activeDrag?.run ? (
                <DragOverlayCard
                  run={activeDrag.run}
                  getWorkoutTypeInfo={getWorkoutTypeInfo}
                  renderIcon={renderIcon}
                  width={dragOriginRect?.width || undefined}
                />
              ) : null}
            </DragOverlay>
          </DndContext>

          {shareSection}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div data-week-view="calendar" className="bg-gray-800 rounded-xl overflow-hidden border-t border-gray-700">
          <div className="divide-y divide-gray-700">
            {renderRow((props) => <StaticWeekRow {...props} />)}
          </div>
        </div>

        {shareSection}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 gap-px bg-gray-700">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} className="bg-gray-800 p-2 h-24 animate-pulse">
            <div className="space-y-2">
              <div className="h-4 bg-gray-600 rounded mb-2" />
              <div className="space-y-1">
                <div className="h-3 bg-gray-600 rounded" />
                <div className="h-3 bg-gray-600 rounded" />
            </div>
        </div>
                                  </div>
                ))}
      </div>
    </div>
  );
};

export default CalendarGrid;